import { Close, Done } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  List,
  ListItem,
} from '@mui/material';
import { SubmitIcon } from '../../../../vectors/index';
import { FC, useContext, useState } from 'react';
import useUpateCompanyPlan from '@features/payments/hooks/useUpdateCompanyPlan';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { PLANS } from '@utils/pricing';
import { authContext } from '@staffedupusa/shared-components';
import { useUpdatePlan } from '@features/payments/hooks/useUpdatePlan';
import { useGetMetrics } from '@features/enterpriseMetrics/hooks/useGetSavedMessages';

type PlanSelectionModalForExpiredProps = {
  isOpen: string;
  hasPaymentMethod: boolean;
  isExpired?: boolean;
  planIds: { company_id?: number; plan_id?: number; new_plan_id: number }[];
  expiredCompanyName?: { companyName?: string; companyId: number }[];
  companyId?: number;
  companyIds?: number[];
  companyName?: string | null;
  handleClose: () => void;
  handleSuccess?: () => void;
};

const PlanSelectionModalForExpired: FC<PlanSelectionModalForExpiredProps> = ({
  isOpen,
  companyId,
  expiredCompanyName,
  planIds,
  handleClose,
  handleSuccess,
  hasPaymentMethod,
}) => {
  const navigate = useNavigate();
  const { authData } = useContext(authContext);
  const billingCycle = authData?.user?.billing_cycle;
  const [errorText, setErrorText] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('4');
  const { updatePlan, isUpdatingPlan } = useUpateCompanyPlan({ setErrorText });
  const { updatePaymentPlan, isUpdatingPaymentPlan } = useUpdatePlan({
    setErrorText,
  });
  const { metricsData } = useGetMetrics({
    enterprise_id: authData?.user?.enterprise?.id,
    selectedCompanyId: 'ALL',
  });
  const companiesData = metricsData?.companies || [];
  const handleSubmit = () => {
    if (!hasPaymentMethod) {
      return navigate('#Payment-Info');
    }
    const options = {
      onSuccess: (data: { status: number; message: string; errors: any }) => {
        if (data.status == 200) {
          setErrorText('');
          toast.success(data.message);
          handleClose();
          handleSuccess?.();
        } else if (data.status === 400) {
          if (data.errors?.length > 0) {
            const errorIds = data.errors.map((obj: {}) =>
              parseInt(Object.keys(obj)[0]),
            );
            const filteredCompanies = companiesData.filter((company) =>
              errorIds.includes(company.company_id),
            );
            const errorHtml = `
              <p style="margin: 0;">The following companies have no <strong>stripe customer</strong>:</p>
              <ul style="margin: 0;">
                ${filteredCompanies.map((c) => `<li><strong>${c.name}</strong></li>`).join('')}
              </ul>
            `;
            setErrorText(errorHtml);
          }
        }
      },
    };
    if (!isOpen.includes('paid') && companyId) {
      updatePlan(
        {
          company_id: companyId,
          update: isOpen,
          plan_type: isOpen === 'upgrade' ? 2 : 1,
        },
        options,
      );
    } else {
      updatePaymentPlan(
        {
          billing_cycle: billingCycle ?? undefined,
          upgrade_plan: isOpen.includes('enterprise') ? true : false,
          downgrade_plan: !isOpen.includes('enterprise') ? true : false,
          switch: isOpen.includes('paid') ? true : false,
          plan_ids: planIds?.map((p) => {
            return {
              company_id: p.company_id,
              plan_id: p.plan_id,
              new_plan_id:
                p.plan_id === 0 || p.plan_id === 999
                  ? Number(selectedPlan)
                  : p.new_plan_id,
            };
          }),
        },
        options,
      );
    }
  };
  return (
    <>
      <Dialog
        open={Boolean(isOpen)}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
          <br />
          <SubmitIcon />
          <br />
          <Typography variant="h4" mt={4}>
            Choose a plan for inactive companies
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography textAlign="center" variant="body1" mt={4}>
            Before we transfer billing for your companies, please select a
            subscription plan for the following companies with expired/no
            billing
          </Typography>
          <Stack mb={2}>
            <List
              sx={{
                listStyleType: 'disc',
                pl: 2,
                '& .MuiListItem-root': {
                  display: 'list-item',
                },
              }}
            >
              {expiredCompanyName?.map((company) => (
                <ListItem disablePadding>
                  <Typography variant="h6">{company.companyName}</Typography>
                </ListItem>
              ))}
            </List>
          </Stack>
          <Stack spacing={2}>
            <FormControl component="fieldset">
              <FormLabel>Select plan</FormLabel>
              <RadioGroup
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                row
              >
                <FormControlLabel
                  value="4"
                  control={<Radio />}
                  label={PLANS.STARTER.NAME}
                />
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label={PLANS.SMARTER.NAME}
                />
                <FormControlLabel
                  value="2"
                  control={<Radio />}
                  label={PLANS.PRO.NAME}
                />
              </RadioGroup>
            </FormControl>
            {errorText && (
              <Alert severity="error">
                <div dangerouslySetInnerHTML={{ __html: errorText }} />
              </Alert>
            )}
            <LoadingButton
              variant="contained"
              startIcon={<Done />}
              onClick={handleSubmit}
              loading={isUpdatingPlan || isUpdatingPaymentPlan}
            >
              Confirm
            </LoadingButton>
            <Button
              startIcon={<Close />}
              variant="outlined"
              color="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PlanSelectionModalForExpired;
